import React, { useEffect, useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { useApi } from '~/contexts';
import { colors } from '~/styles';
import { ChartLabel, ChartWrapper as Wrapper } from './shared';

export default function CostByModel({ start, end }) {
  const api = useApi();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!start || !end) return;
    let cancelled = false;
    const fetchData = async () => {
      setIsLoading(true);
      setData(null);
      try {
        const { data } = await api.admin.aiDashboard.getByModel({ start, end });
        if (!cancelled) setData(data);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [api, start, end]);

  const chartData = useMemo(() => {
    if (!data || data.length === 0) return null;

    return {
      labels: data.map((d) => d.model),
      datasets: [
        {
          label: 'Input Cost',
          data: data.map((d) => d.inputCost),
          backgroundColor: colors.primary,
        },
        {
          label: 'Cached Input Cost',
          data: data.map((d) => d.cachedInputCost),
          backgroundColor: colors.primary50,
        },
        {
          label: 'Output Cost',
          data: data.map((d) => d.outputCost),
          backgroundColor: colors.accent,
        },
      ],
    };
  }, [data]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    scales: {
      x: {
        stacked: true,
        ticks: { callback: (value) => `$${value.toFixed(2)}` },
      },
      y: { stacked: true },
    },
    interaction: { mode: 'index' },
    plugins: {
      tooltip: {
        callbacks: {
          label: (context) => `${context.dataset.label}: $${context.raw.toFixed(2)}`,
          footer: (items) => {
            const total = items.reduce((sum, item) => sum + item.raw, 0);
            return `Total: $${total.toFixed(2)}`;
          },
          afterBody: (contexts) => {
            const idx = contexts[0]?.dataIndex;
            if (idx == null || !data[idx]) return '';
            const d = data[idx];
            const savings = d.fullInputCost - d.inputCost - d.cachedInputCost;
            return savings > 0 ? `Cache savings: $${savings.toFixed(2)}` : '';
          },
        },
      },
    },
  };

  if (isLoading || !chartData) return null;

  return (
    <div>
      <ChartLabel>Cost by Model</ChartLabel>
      <Wrapper>
        <Bar data={chartData} options={options} />
      </Wrapper>
    </div>
  );
}
